"use client";

import { BadgeCheck, Flame } from "lucide-react";
import { useLocale } from "next-intl";

type Fairness = { verdict: "deal" | "fair" | "above"; ratio: number; expected: number; comparables: number } | null | undefined;

/* ─────────────────────────────────────────────────────────────
   Visual "fair price" seal for listing cards.
   Shows only positive verdicts (deal / fair) — never shames a seller.
   ───────────────────────────────────────────────────────────── */
export default function FairPriceSeal({ fairness, className = "", size = "md" }: { fairness: Fairness; className?: string; size?: "sm" | "md" }) {
  const locale = useLocale();
  const ar = locale === "ar";
  if (!fairness || fairness.verdict === "above") return null;

  const deal = fairness.verdict === "deal";
  const pct = Math.round((1 - fairness.ratio) * 100);
  const title = deal
    ? ar ? `أقل من سعر السوق بنحو ${pct}% — بناءً على ${fairness.comparables} عقار مشابه` : `About ${pct}% below market — based on ${fairness.comparables} comparable listings`
    : ar ? `سعر عادل مقارنةً بـ ${fairness.comparables} عقار مشابه في نفس المنطقة` : `Fair price vs ${fairness.comparables} comparable listings in the area`;

  return (
    <span
      title={title}
      className={`group/seal relative inline-flex select-none items-center gap-1 rounded-full font-black shadow-lg ring-2 ring-white/80 backdrop-blur ${
        size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]"
      } ${deal ? "bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-emerald-600/30" : "bg-gradient-to-br from-[#0d3589] to-[#071f52] text-[#f5e4b8] shadow-[#0d3589]/30"} ${className}`}
    >
      {/* seal shine */}
      <span className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-tr from-white/25 via-transparent to-transparent" />
      {deal ? <Flame size={size === "sm" ? 11 : 13} className="relative" /> : <BadgeCheck size={size === "sm" ? 11 : 13} className="relative text-[#d9b25a]" />}
      <span className="relative">
        {deal ? (ar ? `صفقة −${pct}%` : `Deal −${pct}%`) : ar ? "سعر عادل ✓" : "Fair price ✓"}
      </span>
    </span>
  );
}
